import { AUTO, Cursors, HIT_TEST_DISTANCE } from './../../constants';
import { Rect } from './../../math';
import { ConnectionEditUndoUnit } from '../ConnectionEditUndoUnit';
import { Shape } from './../../dom/Shape';
import { deepExtend } from './../../utils/deep-extend';
import { Circle } from './../../svg';
import { AdornerBase } from './AdornerBase';
export class ConnectionEditAdorner extends AdornerBase {
    constructor(connection, options) {
        options = deepExtend({ handles: {} }, options);
        super(connection.diagram, options);
        this.connection = connection;
        const diagram = this.connection.diagram;
        this._ts = diagram.toolService;
        const sp = this.connection.sourcePoint();
        const tp = this.connection.targetPoint();
        this.spVisual = new Circle(deepExtend(this.options.handles, { center: sp }));
        this.epVisual = new Circle(deepExtend(this.options.handles, { center: tp }));
        this.visual.append(this.spVisual);
        this.visual.append(this.epVisual);
    }
    _getCursor() {
        return Cursors.move;
    }
    start(p) {
        this.handle = this._hitTest(p);
        this.startPoint = p;
        this._initialSource = this.connection.source();
        this._initialTarget = this.connection.target();
        switch (this.handle) {
            case -1:
                if (this.connection.targetConnector) {
                    this._ts._connectionManipulation(this.connection, this.connection.targetConnector.shape);
                }
                break;
            case 1:
                if (this.connection.sourceConnector) {
                    this._ts._connectionManipulation(this.connection, this.connection.sourceConnector.shape);
                }
                break;
            default:
                break;
        }
    }
    move(handle, p) {
        const ts = this.diagram.toolService;
        let targetPoint = p;
        // Snap to connector when nearby during drag
        const connector = ts._hoveredConnector;
        if (connector) {
            // Check if we're not trying to connect to the same connector
            const isSourceHandle = handle === -1;
            const isTargetHandle = handle === 1;
            if (isSourceHandle && connector._c !== this.connection.targetConnector) {
                targetPoint = connector._c.position();
            }
            else if (isTargetHandle && connector._c !== this.connection.sourceConnector) {
                targetPoint = connector._c.position();
            }
        }
        switch (handle) {
            case -1:
                this.connection.source(targetPoint);
                break;
            case 1:
                this.connection.target(targetPoint);
                break;
            default: {
                const delta = p.minus(this.startPoint);
                this.startPoint = p;
                if (!this.connection.sourceConnector) {
                    this.connection.source(this.connection.sourcePoint().plus(delta));
                }
                if (!this.connection.targetConnector) {
                    this.connection.target(this.connection.targetPoint().plus(delta));
                }
                break;
            }
        }
        this.refresh();
        return true;
    }
    stop(p) {
        const ts = this.diagram.toolService, item = ts.hoveredItem;
        let target;
        if (ts._hoveredConnector) {
            target = ts._hoveredConnector._c;
        }
        else if (item && item instanceof Shape) {
            target = item.getConnector(AUTO) || item.getConnector(p);
        }
        else {
            target = p;
        }
        if (this.handle === -1) {
            this.connection.source(target);
        }
        else if (this.handle === 1) {
            this.connection.target(target);
        }
        this.handle = undefined;
        this._ts._connectionManipulation();
        return new ConnectionEditUndoUnit(this.connection, this._initialSource, this._initialTarget);
    }
    _hitTest(point) {
        const sourcePoint = this.connection.sourcePoint();
        const targetPoint = this.connection.targetPoint();
        const radiusX = this.options.handles.width / 2 + HIT_TEST_DISTANCE;
        const radiusY = this.options.handles.height / 2 + HIT_TEST_DISTANCE;
        const sourcePointDistance = sourcePoint.distanceTo(point);
        const targetPointDistance = targetPoint.distanceTo(point);
        const sourceHandle = new Rect(sourcePoint.x, sourcePoint.y).inflate(radiusX, radiusY).contains(point);
        const targetHandle = new Rect(targetPoint.x, targetPoint.y).inflate(radiusX, radiusY).contains(point);
        let handle = 0;
        if (sourceHandle && (!targetHandle || sourcePointDistance < targetPointDistance)) {
            handle = -1;
        }
        else if (targetHandle && (!sourceHandle || targetPointDistance < sourcePointDistance)) {
            handle = 1;
        }
        return handle;
    }
    refresh() {
        this.spVisual.redraw({ center: this.diagram.modelToLayer(this.connection.sourcePoint()) });
        this.epVisual.redraw({ center: this.diagram.modelToLayer(this.connection.targetPoint()) });
    }
}
